import i18n from 'i18next';
import {getCurrentLocation, locationErrorMessage} from './location-service';
import {getMyScans} from './scan-service';
import {getDevFlag} from '../config/devSettings';

// Rules for uploading a receipt at a station (the server checks them too):
//  1. the user is within MAX_DISTANCE_M of the station, and
//  2. the user has not already uploaded a receipt there in the last 24 hours.
// Either rule can be switched off for testing in Developer settings.
export const MAX_DISTANCE_M = 400;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Scans in these states count as "already uploaded". Drafts never got
// submitted, and a rejected receipt may be uploaded again.
const COUNTED_STATUSES = ['pending', 'submitted', 'approved'];

const t = (key, fallback, opts) => i18n.t(key, {defaultValue: fallback, ...opts});

// Straight-line distance in metres between two {latitude, longitude} points.
export const distanceInMetres = (a, b) => {
  const toRad = d => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) *
      Math.cos(toRad(b.latitude)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Station objects come from Google Places or our backend, so the position can
// sit in a few places.
const stationPosition = station => {
  const loc = station?.location || station?.geometry?.location || station;
  const latitude = Number(loc?.lat ?? loc?.latitude);
  const longitude = Number(loc?.lng ?? loc?.longitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? {latitude, longitude}
    : null;
};

const scanPlaceId = scan =>
  scan?.placeId || scan?.station?.placeId || scan?.station?.id || null;

const scanTime = scan =>
  new Date(scan?.submittedAt || scan?.createdAt || scan?.receiptAt).getTime();

// True if the user already uploaded a receipt for this station in the last
// 24 h. If the list can't be loaded, say no: the server still enforces it.
const scannedHereRecently = async placeId => {
  if (!placeId) {
    return false;
  }
  const res = await getMyScans({limit: 50});
  if (!res.success) {
    return false;
  }
  const since = Date.now() - ONE_DAY_MS;
  return res.scans.some(
    scan =>
      scanPlaceId(scan) === placeId &&
      COUNTED_STATUSES.includes(String(scan?.status).toLowerCase()) &&
      scanTime(scan) >= since,
  );
};

/**
 * May the user upload a receipt for this station right now?
 * @returns {Promise<{ok: true, location: {latitude: number, longitude: number}}
 *   | {ok: false, message: string}>}
 *   location is where the user is; it is sent with the upload.
 */
export const checkScanAllowed = async station => {
  const [skipDistance, skipDailyLimit] = await Promise.all([
    getDevFlag('skipScanDistance'),
    getDevFlag('skipScanDailyLimit'),
  ]);

  let location = null;
  try {
    const pos = await getCurrentLocation();
    location = {latitude: pos.latitude, longitude: pos.longitude};
  } catch (err) {
    // Without the distance rule a position is nice to have, not required.
    if (!skipDistance) {
      return {ok: false, message: locationErrorMessage(err)};
    }
  }

  const target = stationPosition(station);
  if (!skipDistance && target) {
    const metres = distanceInMetres(location, target);
    if (metres > MAX_DISTANCE_M) {
      return {
        ok: false,
        message: t(
          'root.ScanRules.tooFar',
          'You need to be at the station to upload a receipt (within {{max}} m). You are about {{distance}} away.',
          {
            max: MAX_DISTANCE_M,
            distance:
              metres >= 1000
                ? `${(metres / 1000).toFixed(1)} km`
                : `${Math.round(metres)} m`,
          },
        ),
      };
    }
  }

  if (!skipDailyLimit && (await scannedHereRecently(station?.id))) {
    return {
      ok: false,
      message: t(
        'root.ScanRules.alreadyScanned',
        'You already uploaded a receipt for this station today. You can upload another one 24 hours after your last one.',
      ),
    };
  }

  return {ok: true, location};
};
